/* Roles and permissions an administrator can assign.
 *
 * ROLES must be exactly user_access.VALID_ROLES; a unit test holds the two
 * together. The backend validates every value regardless -- this list only
 * decides what the form offers.
 */

export const ROLES = [
  { value: 'staff',     label: 'Staff',            description: 'Works in the modules granted below' },
  { value: 'read_only', label: 'Read only',        description: 'Sees the modules granted below; cannot change anything' },
  { value: 'cpa',       label: 'CPA — both entities', description: 'Read-only accounts for Eyvo USA and Eyvo India' },
  { value: 'cpa_usa',   label: 'CPA — Eyvo USA',   description: 'Read-only accounts for Eyvo USA only' },
  { value: 'cpa_india', label: 'CPA — Eyvo India', description: 'Read-only accounts for Eyvo India only' },
  { value: 'admin',     label: 'Administrator',    description: 'Everything, including Administration → Users' },
];

export const roleLabel = (role) => ROLES.find(r => r.value === role)?.label || role;

/* An active user holding none of these can sign in and use nothing -- not
 * even the dashboard, which is granted here like everything else. Nothing is
 * checked by default, and saving a user with nothing checked is allowed: it
 * records someone who may be given access later. */
export const PERMISSION_MODULES = [
  { key: 'dashboard',      label: 'Dashboard',             description: 'The Dashboard screen. Its figures also need both Accounts entities' },
  { key: 'accounts_usa',   label: 'Accounts — Eyvo USA',   description: 'Transactions, journals, reports and bank connections for Eyvo USA' },
  { key: 'accounts_india', label: 'Accounts — Eyvo India', description: 'Transactions, journals and reports for Eyvo India' },
  { key: 'invoices',       label: 'Invoices',              description: 'Create, edit and send invoices' },
  { key: 'clients',        label: 'Clients',               description: 'View and manage client records' },
  { key: 'suppliers',      label: 'Suppliers',             description: 'View and manage supplier records' },
  { key: 'products',       label: 'Products',              description: 'View and manage the product catalog' },
  { key: 'quotes',         label: 'Quotes / Proposals',    description: 'Create, edit and send proposals' },
  { key: 'financials',     label: 'Financials',            description: 'Monetary amounts, totals and payment status' },
  { key: 'reminders',      label: 'Reminders',             description: 'Configure and trigger payment reminders' },
];

/** Permissions the backend still accepts that the form does not offer.
 *  With PERMISSION_MODULES, exactly user_access.ALL_PERMISSIONS. */
export const OTHER_PERMISSIONS = ['accounts', 'users', 'crm'];

const KNOWN_PERMISSIONS = new Set([...PERMISSION_MODULES.map(m => m.key), ...OTHER_PERMISSIONS]);
/** Roles that are themselves a grant of entity access (CPA_ENTITY_MAP). */
const ROLE_GRANTS = new Set(['cpa', 'cpa_usa', 'cpa_india']);

/** True when this role and these permissions give no FinOps access at all.
 *  Mirrors user_access.has_app_access for an active account: staff and
 *  read-only roles grant nothing by themselves. */
export const grantsNothing = (role, permissions) =>
  role !== 'admin' && !ROLE_GRANTS.has(role)
  && !(permissions || []).some(p => KNOWN_PERMISSIONS.has(p));

export const NO_ACCESS_HINT = 'No access assigned: this person can sign in with Google but cannot use any part of FinOps until a permission is granted.';

/** Mirrors the backend's address check, so a bad address is caught before it
 *  is sent. The backend still decides. */
const EMAIL_RE = /^[a-z0-9](?:[a-z0-9._%+'-]{0,62}[a-z0-9_])?@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/;

export function emailProblem(raw) {
  const email = (raw || '').trim().toLowerCase();
  if (!email) return 'Enter an email address';
  if (!EMAIL_RE.test(email) || email.includes('..')) return 'Enter a valid email address';
  if (!email.endsWith('@eyvo.com')) return 'Must be an @eyvo.com Google account';
  return '';
}

/** A readable reason from an API error. */
export const apiError = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};
